import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Plus, CircleCheck, Circle, CalendarDays, MessageCircle, Paperclip, X, Link2, Copy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { TASK_STATUSES, PRIORITIES, STATUS_MAP, PRIORITY_MAP, isOverdue, fmtDate, TASK_RECURRENCES } from "@/lib/taskConstants";
import { UserAvatar } from "@/components/Avatars";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__none__";

export const TaskDetailSheet = ({ task, subtasks, members, projectId, onClose, onSelectTask, allTasks = [] }) => {
  const availableDeps = useMemo(
    () => allTasks.filter((x) => x.id !== task?.id && !(task?.dependency_ids || []).includes(x.id)),
    [allTasks, task]
  );
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [newSubtask, setNewSubtask] = useState("");

  const { data: attachments = [] } = useQuery({
    queryKey: ["task-files", task?.id],
    queryFn: async () => (await api.get(`/workspaces/${task.workspace_id}/files`, {
      params: { entity_type: "task", entity_id: task.id },
    })).data,
    enabled: !!task,
  });

  const { id: taskId, title: taskTitle, description: taskDescription } = task || {};
  useEffect(() => {
    if (taskId) {
      setTitle(taskTitle);
      setDescription(taskDescription || "");
    }
  }, [taskId, taskTitle, taskDescription]);

  if (!task) return null;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["tasks", projectId] });

  const update = async (payload) => {
    try {
      await api.put(`/tasks/${task.id}`, payload);
      invalidate();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/tasks/${task.id}`);
      invalidate();
      toast.success("Tarea eliminada");
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addSubtask = async () => {
    if (!newSubtask.trim()) return;
    try {
      await api.post(`/projects/${projectId}/tasks`, { title: newSubtask, parent_task_id: task.id });
      setNewSubtask("");
      invalidate();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleSubtask = async (st) => {
    try {
      await api.put(`/tasks/${st.id}`, { status: st.status === "done" ? "todo" : "done" });
      invalidate();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeSubtask = async (st) => {
    try {
      await api.delete(`/tasks/${st.id}`);
      invalidate();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const isSubtask = !!task.parent_task_id;
  const overdue = isOverdue(task);

  return (
    <Sheet open={!!task} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="task-detail-sheet">
        <SheetHeader className="space-y-1">
          <div className="flex items-center gap-2">
            {isSubtask && <Badge variant="outline" className="text-xs">Subtarea</Badge>}
            {overdue && <Badge variant="destructive" className="text-xs" data-testid="task-overdue-badge">Vencida</Badge>}
          </div>
          <SheetTitle className="sr-only">Detalle de tarea</SheetTitle>
          <SheetDescription className="sr-only">Edita la tarea</SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-2">
          <Input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== task.title && update({ title })}
            className="text-lg font-semibold font-heading border-transparent hover:border-input focus:border-input px-2 -mx-2" />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Estado</Label>
              <Select value={task.status} onValueChange={(v) => update({ status: v })}>
                <SelectTrigger className="h-9" data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Prioridad</Label>
              <Select value={task.priority} onValueChange={(v) => update({ priority: v })}>
                <SelectTrigger className="h-9" data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                        {p.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Responsable</Label>
              <Select value={task.assignee_id || NONE}
                onValueChange={(v) => update(v === NONE ? { unassign: true } : { assignee_id: v })}>
                <SelectTrigger className="h-9" data-testid="task-assignee-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Sin responsable</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Fechas</Label>
              <div className="flex items-center gap-1">
                <Input type="date" data-testid="task-start-date-input" value={task.start_date || ""}
                  onChange={(e) => update(e.target.value ? { start_date: e.target.value } : { clear_start_date: true })}
                  className="h-9 text-xs px-2" />
                <span className="text-muted-foreground text-xs">→</span>
                <Input type="date" data-testid="task-due-date-input" value={task.due_date || ""}
                  onChange={(e) => update(e.target.value ? { due_date: e.target.value } : { clear_due_date: true })}
                  className="h-9 text-xs px-2" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Recompensa (€)</Label>
              <div className="flex items-center gap-1.5">
                <Input type="number" min="0" step="0.5" data-testid="task-reward-input"
                  key={`${task.id}-${task.reward_amount ?? ""}`}
                  defaultValue={task.reward_amount ?? ""}
                  placeholder="0"
                  disabled={!!task.reward_paid_at}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!e.target.value) {
                      if (task.reward_amount) update({ clear_reward: true });
                    } else if (v >= 0 && v !== task.reward_amount) {
                      update({ reward_amount: v });
                    }
                  }}
                  className="h-9 text-sm" />
                {task.reward_paid_at && (
                  <Badge variant="secondary" className="text-[10px] shrink-0" data-testid="task-reward-paid-badge">💰 Abonada</Badge>
                )}
              </div>
              {task.reward_amount > 0 && !task.reward_paid_at && (
                <p className="text-[11px] text-muted-foreground">Se abonará a la hucha del responsable al completarla.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Repetición</Label>
              <Select value={task.recurrence || "none"} onValueChange={(v) => update({ recurrence: v })}>
                <SelectTrigger className="h-9" data-testid="task-recurrence-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_RECURRENCES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.value !== "none" ? "🔁 " : ""}{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {task.recurrence && task.recurrence !== "none" && (
                <p className="text-[11px] text-muted-foreground">Al completarla se creará la siguiente automáticamente.</p>
              )}
            </div>
          </div>

          {!task.parent_task_id && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Link2 className="h-3 w-3" /> Depende de</Label>
              <div className="flex flex-wrap items-center gap-1.5">
                {(task.dependency_ids || []).map((d) => {
                  const dt = allTasks.find((x) => x.id === d);
                  return dt ? (
                    <Badge key={d} variant="outline" className="gap-1 text-xs" data-testid={`task-dep-${d}`}>
                      {dt.status === "done" ? "✅" : "⏳"} {dt.title}
                      <button onClick={() => update({ dependency_ids: task.dependency_ids.filter((x) => x !== d) })}
                        data-testid={`task-dep-remove-${d}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ) : null;
                })}
                <Select value="" onValueChange={(v) => update({ dependency_ids: [...(task.dependency_ids || []), v] })}>
                  <SelectTrigger className="h-7 w-48 text-xs" data-testid="task-dep-select">
                    <SelectValue placeholder="+ Añadir dependencia" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDeps.map((x) => (
                      <SelectItem key={x.id} value={x.id}>{x.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(task.dependency_ids || []).length > 0 && (
                <p className="text-[11px] text-muted-foreground">No se podrá completar hasta que sus dependencias estén completadas.</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Descripción</Label>
            <Textarea data-testid="task-description-input" value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => description !== (task.description || "") && update({ description })}
              rows={3} placeholder="Añade detalles..." maxLength={2000} />
          </div>

          {!isSubtask && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">
                Subtareas {subtasks.length > 0 && `(${subtasks.filter((s) => s.status === "done").length}/${subtasks.length})`}
              </Label>
              <div className="space-y-1">
                {subtasks.map((st) => (
                  <div key={st.id} className="flex items-center gap-2 group rounded-lg px-2 py-1.5 hover:bg-secondary"
                    data-testid={`subtask-row-${st.id}`}>
                    <button onClick={() => toggleSubtask(st)} data-testid={`subtask-toggle-${st.id}`} className="shrink-0">
                      {st.status === "done"
                        ? <CircleCheck className="h-4 w-4 text-[#1E8E3E]" />
                        : <Circle className="h-4 w-4 text-muted-foreground" />}
                    </button>
                    <button className={`text-sm flex-1 text-left truncate ${st.status === "done" ? "line-through text-muted-foreground" : ""}`}
                      onClick={() => onSelectTask(st.id)} data-testid={`subtask-open-${st.id}`}>
                      {st.title}
                    </button>
                    {st.due_date && (
                      <span className={`text-xs flex items-center gap-1 shrink-0 ${isOverdue(st) ? "text-destructive" : "text-muted-foreground"}`}>
                        <CalendarDays className="h-3 w-3" />{fmtDate(st.due_date)}
                      </span>
                    )}
                    {st.assignee_id && (() => {
                      const m = members.find((mm) => mm.profile_id === st.assignee_id);
                      return m ? <UserAvatar name={m.name} color={m.avatar_color} size="sm" className="!h-5 !w-5 !text-[9px]" /> : null;
                    })()}
                    <button onClick={() => removeSubtask(st)} data-testid={`subtask-delete-${st.id}`}
                      className="opacity-0 group-hover:opacity-100 text-destructive shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input data-testid="subtask-new-input" value={newSubtask} onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addSubtask()}
                  placeholder="Nueva subtarea..." className="h-8 text-sm" maxLength={300} />
                <Button size="sm" variant="outline" className="h-8" data-testid="subtask-add-btn" onClick={addSubtask}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}

          {isSubtask && (
            <Button variant="ghost" size="sm" className="text-xs" data-testid="subtask-back-to-parent-btn"
              onClick={() => onSelectTask(task.parent_task_id)}>
              ← Volver a la tarea principal
            </Button>
          )}

          <div className="pt-4 border-t space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Adjuntos ({attachments.length})
              </Label>
              <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="task-attach-btn"
                onClick={() => fileRef.current?.click()}>
                <Plus className="h-3 w-3 mr-1" /> Adjuntar
              </Button>
              <input ref={fileRef} type="file" hidden data-testid="task-attach-input" onChange={async (e) => {
                const f = e.target.files[0];
                if (!f) return;
                const form = new FormData();
                form.append("file", f);
                form.append("entity_type", "task");
                form.append("entity_id", task.id);
                try {
                  await api.post(`/workspaces/${task.workspace_id}/files`, form);
                  qc.invalidateQueries({ queryKey: ["task-files", task.id] });
                  toast.success("Archivo adjuntado");
                } catch (err) {
                  toast.error(apiError(err));
                }
                e.target.value = "";
              }} />
            </div>
            {attachments.map((f) => (
              <a key={f.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download`} download={f.name}
                data-testid={`task-attachment-${f.id}`}
                className="flex items-center gap-2 text-xs border rounded-lg px-3 py-2 hover:bg-secondary">
                <Paperclip className="h-3 w-3 text-muted-foreground" />
                <span className="flex-1 truncate font-medium">{f.name}</span>
              </a>
            ))}
            <Button variant="outline" size="sm" className="w-full text-xs" data-testid="task-open-chat-btn"
              onClick={async () => {
                const { data } = await api.get("/conversations/for-entity", {
                  params: { workspace_id: task.workspace_id, entity_type: "task", entity_id: task.id },
                });
                navigate(`/space/${task.workspace_id}/chat?c=${data.id}`);
              }}>
              <MessageCircle className="h-3.5 w-3.5 mr-1" /> Abrir conversación de la tarea
            </Button>
          </div>

          <div className="pt-4 border-t flex justify-between items-center">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_MAP[task.status]?.color }} />
              {STATUS_MAP[task.status]?.label} · {PRIORITY_MAP[task.priority]?.label}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" data-testid="task-duplicate-btn" onClick={async () => {
                try {
                  await api.post(`/tasks/${task.id}/duplicate`);
                  qc.invalidateQueries({ queryKey: ["tasks", projectId] });
                  toast.success("Tarea duplicada");
                } catch (e) { toast.error(apiError(e)); }
              }}>
                <Copy className="h-4 w-4 mr-1" /> Duplicar
              </Button>
              <Button variant="ghost" size="sm" className="text-destructive" data-testid="task-delete-btn" onClick={remove}>
                <Trash2 className="h-4 w-4 mr-1" /> Eliminar
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
